import { debounce } from './helpers';

/**
 * Universal Timeline Controller
 *
 * Handles staggered IntersectionObserver animations, scroll-based vertical
 * spine progress calculations, and mobile horizontal swipe item state tracking.
 *
 * @param {Object} options Configuration options for the timeline system.
 * @param {string} options.timelineSelector Selector for the timeline container.
 * @param {string} options.prefix Class prefix used for timeline components (e.g. 'timeline' or 'environmental-timeline').
 * @returns {Object} Exposes helpers like updateSpineColors, updateMobileProgress, and observeAnimateItems.
 */
export const initTimeline = ({ timelineSelector, prefix }) => {
  const timeline = document.querySelector(timelineSelector);
  if (!timeline) return {};

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const markers = document.querySelectorAll(`.${prefix}__marker`);
  const segments = document.querySelectorAll(`.${prefix}__segment`);
  const mobileScroll = document.querySelector(`.${prefix}__mobile-scroll`);
  const mobileItems = mobileScroll
    ? mobileScroll.querySelectorAll(`.${prefix}__mobile-item`)
    : [];

  let intersectQueue = [];
  let intersectTimeout = null;

  // ─── Scroll-based color animation for spine markers and segments ─────
  const updateSpineColors = () => {
    if (prefersReducedMotion) {
      markers.forEach((marker) => {
        marker.style.setProperty('--marker-progress', '1');
        marker.classList.add(`${prefix}__marker--active`);
      });
      segments.forEach((segment) => {
        segment.style.setProperty('--segment-progress', '1');
      });
      return;
    }

    const triggerPoint = window.innerHeight * 0.55; // 55% from the top of the viewport

    // Update markers progress
    markers.forEach((marker) => {
      const rect = marker.getBoundingClientRect();
      let progress = 0;

      if (rect.bottom <= triggerPoint) {
        progress = 1;
      } else if (rect.top >= triggerPoint) {
        progress = 0;
      } else {
        progress = (triggerPoint - rect.top) / rect.height;
      }

      marker.style.setProperty('--marker-progress', progress);

      // Toggle active class
      if (progress > 0.5) {
        marker.classList.add(`${prefix}__marker--active`);
      } else {
        marker.classList.remove(`${prefix}__marker--active`);
      }
    });

    // Update segments progress
    segments.forEach((segment) => {
      const rect = segment.getBoundingClientRect();
      let progress = 0;

      if (rect.bottom <= triggerPoint) {
        progress = 1;
      } else if (rect.top >= triggerPoint) {
        progress = 0;
      } else {
        progress = (triggerPoint - rect.top) / rect.height;
      }

      segment.style.setProperty('--segment-progress', progress);
    });
  };

  // ─── Mobile horizontal scroll tracking ────────────────────────────────
  const updateMobileProgress = () => {
    if (!mobileScroll || !mobileItems.length) return;

    const containerWidth = mobileScroll.clientWidth;
    const scrollLeft = mobileScroll.scrollLeft;

    mobileItems.forEach((item, index) => {
      const itemLeft = item.offsetLeft;
      const triggerPos = scrollLeft + containerWidth * 0.45;

      if (triggerPos >= itemLeft && item.classList.contains('is-animated')) {
        const nextItem = mobileItems[index + 1];
        const isNextActive =
          nextItem &&
          triggerPos >= nextItem.offsetLeft &&
          nextItem.classList.contains('is-animated');

        if (isNextActive) {
          item.classList.add(`${prefix}__mobile-item--passed`);
          item.classList.remove(`${prefix}__mobile-item--active`);
        } else {
          item.classList.add(`${prefix}__mobile-item--active`);
          item.classList.remove(`${prefix}__mobile-item--passed`);
        }
      } else {
        item.classList.remove(
          `${prefix}__mobile-item--active`,
          `${prefix}__mobile-item--passed`,
        );
      }
    });
  };

  // ─── IntersectionObserver for scroll animations ───────────────────────
  const observer = new IntersectionObserver(
    (entries) => {
      const intersectingEntries = entries.filter(
        (entry) => entry.isIntersecting,
      );
      if (intersectingEntries.length === 0) return;

      intersectingEntries.forEach((entry) => {
        const item = entry.target;
        observer.unobserve(item);
        if (!intersectQueue.includes(item)) {
          intersectQueue.push(item);
        }
      });

      if (intersectTimeout) {
        clearTimeout(intersectTimeout);
      }

      intersectTimeout = setTimeout(() => {
        // Sort items by data-index to preserve top-to-bottom staggering order
        intersectQueue.sort((a, b) => {
          const indexA = parseInt(a.dataset.index, 10);
          const indexB = parseInt(b.dataset.index, 10);
          return indexA - indexB;
        });

        // Animate each with a relative staggered delay
        intersectQueue.forEach((item, relativeIndex) => {
          setTimeout(() => {
            item.classList.add('is-animated');
            if (item.classList.contains(`${prefix}__mobile-item`)) {
              updateMobileProgress();
            }
          }, relativeIndex * 150);
        });

        // Reset queue
        intersectQueue = [];
        intersectTimeout = null;
      }, 20);
    },
    { threshold: 0.2 },
  );

  const observeAnimateItems = (items, animateImmediately = false) => {
    if (prefersReducedMotion) {
      items.forEach((item) => {
        item.classList.add('is-animated');
      });
      return;
    }
    items.forEach((item) => {
      observer.observe(item);
    });
  };

  // Register listeners
  let scrollThrottleTimeout;
  const onScroll = () => {
    if (scrollThrottleTimeout) {
      cancelAnimationFrame(scrollThrottleTimeout);
    }
    scrollThrottleTimeout = requestAnimationFrame(updateSpineColors);
  };

  if (!prefersReducedMotion) {
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
  }

  if (mobileScroll) {
    mobileScroll.addEventListener('scroll', updateMobileProgress);
    window.addEventListener('resize', debounce(updateMobileProgress, 150));
  }

  // Initial calls deferred to post-initial-render to prevent blocking main thread
  requestAnimationFrame(() => {
    setTimeout(() => {
      updateSpineColors();
      updateMobileProgress();
    }, 50);
  });

  return {
    updateSpineColors,
    updateMobileProgress,
    observeAnimateItems,
  };
};
